import {
  MaintenanceGuide,
  MaintenanceGuideApiResponseData,
  MaintenanceGuideSavePayload,
  UseMaintenanceGuidesOptions,
} from 'src/SharedCustomHooks/UseMaintenanceGuides/types';
import dayjs from 'dayjs';

// Parses the API response into our local MaintenanceGuide format.
const parseGuideFromApiResponse = (data: any): MaintenanceGuide => {
  let parsedData: any = {};

  if (data.data) {
    try {
      parsedData =
        typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
    } catch (e) {
      console.warn('Failed to parse guide data JSON:', e);
    }
  }

  return {
    id: data.id,
    itemId: data.itemId,
    title: parsedData.title || data.title || data.name || 'Untitled',
    body: parsedData.body || data.body || '',
    author: parsedData.author ||
      data.author || {
        id: '',
        firstName: '',
        lastName: '',
      },
    createdAt:
      parsedData.createdAt || data.createdAt || new Date().toISOString(),
    appointment: parsedData.appointment,
    toggle: parsedData.toggle,
    repeatType: parsedData.repeatType || data.repeatType,
    repeatEvery: parsedData.repeatEvery || data.repeatEvery,
    repeatTypeEvery: parsedData.repeatTypeEvery || data.repeatTypeEvery,
    IsSuiteWarrantyStartDate:
      parsedData.IsSuiteWarrantyStartDate ??
      data.isSuiteWarrantyStartDate ??
      false,
  };
};

// Parses the response from the new lightweight endpoint.
const parseGuideFromNewEndpoint = (
  item: MaintenanceGuideApiResponseData
): MaintenanceGuide => {
  let content: any = { title: '', body: '', author: null };

  try {
    if (item.data) {
      content =
        typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
    }
  } catch (e) {
    console.error('Failed to parse nested maintenance guide content:', e);
  }

  const hasReminder: boolean = !!item.time;

  return {
    id: item.id,
    itemId: item.itemId,
    title: content.title || 'Untitled',
    body: content.body || '',
    author: content.author || { id: '', firstName: '', lastName: '' },
    createdAt: item.createdOn,
    toggle: hasReminder,
    appointment: hasReminder
      ? {
          date: item.reminderDate ? new Date(item.reminderDate) : null,
          time: item.time ? dayjs(item.time, 'HH:mm').toDate() : null,
        }
      : undefined,
    repeatType: content.repeatType,
    repeatEvery: content.repeatEvery,
    repeatTypeEvery: content.repeatTypeEvery,
    IsSuiteWarrantyStartDate: item.isSuiteWarrantyStartDate,
  };
};

// Builds the data payload for saving/updating a guide.
const buildSaveRequestPayload = (
  guide: Partial<MaintenanceGuide>,
  itemId: string,
  options: UseMaintenanceGuidesOptions
): MaintenanceGuideSavePayload => {
  const guideData: any = {
    title: guide.title,
    body: guide.body,
    author: guide.author || {
      id: options.userId,
      firstName: options.userFirstName || '',
      lastName: options.userLastName || '',
    },
    createdAt: guide.createdAt || new Date().toISOString(),
    appointment: guide.appointment,
    toggle: guide.toggle,
    repeatType: guide.repeatType,
    repeatEvery: guide.repeatEvery,
    repeatTypeEvery: guide.repeatTypeEvery,
    IsSuiteWarrantyStartDate: guide.IsSuiteWarrantyStartDate,
  };

  let payload: any = {
    ItemId: itemId,
    Name: 'MAINTENANCEGUIDE',
    Type: 'MAINTENANCEGUIDE',
    Data: JSON.stringify(guideData),
    UserId: options.userId,
    MaintenanceReminderFrom: options.source,
    IsSuiteWarrantyStartDate: guide.IsSuiteWarrantyStartDate ?? false,
    Time: guide.appointment?.time?.toISOString(),
  };

  // Add reminder-specific fields if appointment is set.
  if (guide.appointment) {
    let reminderDate: Date = new Date();

    if (guide.IsSuiteWarrantyStartDate === false && guide.appointment.date) {
      const date = new Date(guide.appointment.date);

      if (guide.appointment.time) {
        const time = new Date(guide.appointment.time);

        date.setHours(time.getHours());
        date.setMinutes(time.getMinutes());
      }

      reminderDate = date;
    }

    payload = {
      ...payload,
      ReminderDate: reminderDate,
      repeatType: guide.repeatType || '',
      toggle: guide.toggle,
    };

    if (guide.repeatType === 'Custom') {
      payload = {
        ...payload,
        repeatEvery: Number(guide.repeatEvery),
        repeatTypeEvery: guide.repeatTypeEvery,
      };
    }
  }

  return payload;
};

export {
  parseGuideFromApiResponse,
  buildSaveRequestPayload,
  parseGuideFromNewEndpoint,
};
